import { IFacetSelection, IFacetSelections } from '../../types/facets'
import { AlgoliaFilterValue, AlgoliaFilterValueType } from '../../lib/algolia'

export const algoliaFacets: {
  [id: string]: {
    attribute?: string
    values?: { [id: string]: AlgoliaFilterValue }
  }
} = {
  brand: {
    attribute: 'variant.facets.brand-name',
  },
  colour: {
    attribute: 'variant.facets.colour-group',
  },
  price: {
    attribute: 'variant.price.effectivePrice',
    values: {
      '0-50': {
        type: AlgoliaFilterValueType.NUMERIC_RANGE,
        value: {
          min: 0,
          max: 50,
        },
      },
      '50-100': {
        type: AlgoliaFilterValueType.NUMERIC_RANGE,
        value: {
          min: 50,
          max: 100,
        },
      },
      '100-200': {
        type: AlgoliaFilterValueType.NUMERIC_RANGE,
        value: {
          min: 100,
          max: 200,
        },
      },
      '200-above': {
        type: AlgoliaFilterValueType.NUMERIC_RANGE,
        value: {
          min: 200,
        },
      },
    },
  },
  store: {
    attribute: 'shop.name',
  },
}

export function makeCategoryFilters(categoryIdPath: string[]): {
  attribute: string
  values: AlgoliaFilterValue[]
}[] {
  if (categoryIdPath.length === 0) {
    return []
  }

  return [
    {
      attribute: 'category',
      values: [
        {
          type: AlgoliaFilterValueType.MATCH,
          value: categoryIdPath.join('/'),
        },
      ],
    },
  ]
}

function makeFacetFilter(facet: IFacetSelection): {
  attribute: string
  values: AlgoliaFilterValue[]
} {
  const algoliaFacet = algoliaFacets[facet.id]
  return {
    attribute: algoliaFacet?.attribute ?? 'variant.facets.' + facet.id,
    values: facet.selections.map(
      option =>
        algoliaFacet?.values?.[option] ?? {
          type: AlgoliaFilterValueType.MATCH,
          value: option,
        }
    ),
  }
}

export function makeProductFacetFilters({
  facetSelections,
  onSale,
}: {
  facetSelections: IFacetSelections
  onSale: boolean
}): {
  attribute: string
  values: AlgoliaFilterValue[]
}[] {
  return [
    ...makeOnSaleFacetFilter(onSale),
    ...facetSelections.map(makeFacetFilter),
  ]
}

export function makeOnSaleFacetFilter(onSale: boolean): {
  attribute: string
  values: AlgoliaFilterValue[]
}[] {
  return (
    (onSale && [
      {
        attribute: 'virtual.onSaleOrHasCampaign',
        values: [
          {
            type: AlgoliaFilterValueType.MATCH,
            value: 'true',
          } as AlgoliaFilterValue,
        ],
      },
    ]) ||
    []
  )
}

export function makeProductTypeFilters({
  productTypes,
}: {
  productTypes: string[]
}): {
  attribute: string
  values: AlgoliaFilterValue[]
}[] {
  return [
    {
      attribute: 'variant.facets.' + 'type',
      values: productTypes.map(
        option =>
          algoliaFacets['type']?.values?.[option] ?? {
            type: AlgoliaFilterValueType.MATCH,
            value: option,
          }
      ),
    },
  ]
}

export function makeBrandFilter(brandSlug: string | undefined): {
  attribute: string
  values: AlgoliaFilterValue[]
}[] {
  if (!brandSlug) {
    return []
  }

  return [
    {
      attribute: 'brand.slug',
      values: [
        {
          type: AlgoliaFilterValueType.MATCH,
          value: brandSlug,
        },
      ],
    },
  ]
}

export function makeProductCollectionsFilters(collectionName: string): {
  attribute: string
  values: AlgoliaFilterValue[]
}[] {
  if (collectionName.length === 0) {
    return []
  }

  return [
    {
      attribute: `collections.${collectionName}`,
      values: [
        {
          type: AlgoliaFilterValueType.NUMERIC_RANGE,
          value: {
            min: 0,
          },
        },
      ],
    },
  ]
}

export function makeCampaignFilter(talonOneId: string | number): {
  attribute: string
  values: AlgoliaFilterValue[]
} {
  return {
    attribute: 'promotionsVariantCampaignIds',
    values: [
      { type: AlgoliaFilterValueType.MATCH, value: `talon-one_${talonOneId}` },
    ],
  }
}

export function makeProductFilters(
  categoryIdPath: string[],
  {
    facetSelections,
    onSale,
    productTypes,
  }: {
    facetSelections: IFacetSelections
    onSale: boolean
    productTypes: string[]
  },
  otherFilters?: {
    attribute: string
    values: AlgoliaFilterValue[]
  }[]
): {
  attribute: string
  values: AlgoliaFilterValue[]
}[] {
  return [
    ...makeCategoryFilters(categoryIdPath),
    ...makeProductFacetFilters({ facetSelections, onSale }),
    ...makeProductTypeFilters({ productTypes }),
    ...(otherFilters ? otherFilters : []),
  ]
}
